import { pdf } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { business_integrations, payments } from "lobbie";
import { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, ButtonProps, Dropdown, DropdownProps } from "react-bootstrap";
import { FiDownload, FiMoreVertical } from "react-icons/fi";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import { useLobbieLocationsRedux } from "src/hooks/locations/useLobbieLocations";
import { InvoiceCharge } from "src/redux/slices/drawers/invoiceDrawerSlice";
import { EAccountCountry } from "src/types/global_enums";
import Invoice from "./billing/Invoice";

// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#legacy-pdfjs-worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();

interface ViewInvoiceButtonProps {
    buttonProps: ButtonProps;
    toggleProps: ButtonProps;
    onViewInvoice: () => void;
    onPrint: () => void;
    isHeaderVariant?: boolean;
    isLoading: boolean;
    receiptDetails?: business_integrations.IPaymentRequest;
}

const ViewInvoiceButton: React.FC<ViewInvoiceButtonProps> = ({
    buttonProps,
    toggleProps,
    onViewInvoice,
    onPrint,
    isHeaderVariant = false,
    isLoading,
    receiptDetails,
}) => {
    const StatusPill = () => (
        <span
            className="ms-2 px-2 py-0 small rounded"
            style={{
                backgroundColor: "#EEF4FF",
                color: "#002C6A",
            }}
        >
            {isLoading ? "Loading" : "Draft"}
        </span>
    );

    // No drop-down for specific case in modal
    if (isHeaderVariant) {
        return (
            <Button {...buttonProps} onClick={onPrint}>
                <FiDownload />
                {`Download ${receiptDetails ? "Receipt" : "Invoice"}`}
            </Button>
        );
    }

    // All users when not open in modal
    return (
        <Dropdown as={ButtonGroup}>
            <Button {...buttonProps} onClick={isHeaderVariant ? onPrint : onViewInvoice}>
                <span className="small">
                    {isHeaderVariant ? "Download Invoice" : "View Invoice"}
                </span>
                <StatusPill />
            </Button>
            <Dropdown.Toggle {...toggleProps}>
                <FiMoreVertical size={20} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="py-2">
                {!isHeaderVariant && (
                    <Dropdown.Item className="px-3">
                        <Button
                            variant="none"
                            className="p-0 text-muted shadow-none"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                onPrint?.();
                            }}
                        >
                            <FiDownload className="me-2" />
                            Download Invoice
                        </Button>
                    </Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

interface IProps extends Omit<DropdownProps, "children"> {
    buttonProps: ButtonProps;
    toggleProps: ButtonProps;
    receiptDetails?: business_integrations.IPaymentRequest;
    invoiceDetails: business_integrations.IInvoiceDetails;
    services: InvoiceCharge[];
    items: InvoiceCharge[];
}

const transformExistingPaymentDataIntoPrintFormat = (
    invoiceDetails: business_integrations.IInvoiceDetails,
    services: InvoiceCharge[],
    items: InvoiceCharge[],
    locations: {
        id: number;
        timeZone: string;
        phoneNumber: string;
        branding: { logoS3Url?: string };
        address: { fullAddress: string };
    }[],
    account: { accountCountry: EAccountCountry; name: string; branding: { logoS3Url?: string } },
    receiptDetails?: business_integrations.IPaymentRequest,
): payments.print.IInvoicePrintData => {
    const location = locations.find((location_) => location_.id === invoiceDetails.locationId);
    if (!location) throw new Error("Location not found");
    // Transform account patient
    const accountPatient: payments.print.IAccountPatient = {
        id: invoiceDetails.accountPatient.id,
        name: invoiceDetails.accountPatient.name,
        email: invoiceDetails.accountPatient.email,
        phone: invoiceDetails.accountPatient.phone,
        address: invoiceDetails.accountPatient.address,
    };
    // Transform provider
    let provider: payments.print.IStaff | undefined;
    if (invoiceDetails.provider) {
        provider = {
            name: invoiceDetails.provider.name,
            npiNumber: invoiceDetails.provider.npiNumber,
        };
    }
    // Transform tax settings
    let taxSettings: payments.print.ITaxSettings | undefined;
    if (invoiceDetails.taxSettings) {
        taxSettings = {
            active: invoiceDetails.taxSettings.active,
            salesTax: invoiceDetails.taxSettings.salesTax || 0,
            provinceName: invoiceDetails.taxSettings.provinceName,
            countryCode: account.accountCountry,
        };
    }
    // Transform line items
    const invoiceLineItems: payments.print.IInvoiceLineItem[] = invoiceDetails.lineItems.map(
        (item) => {
            const service_ = services.find(
                (s) => Number(s.careResourceId) === Number(item.careResourceId),
            );
            const item_ = items.find(
                (i) => Number(i.careResourceId) === Number(item.careResourceId),
            );
            if (!service_ && !item_) {
                throw new Error("Service or item not found");
            }
            let code = "";
            if (service_ && service_.code) {
                code = service_.code;
            } else if (item_ && item_.code) {
                code = item_.code;
            }
            // Calculate tax rate: use the sales tax rate if this item applies tax, otherwise 0
            const taxRate =
                item.applyTax && invoiceDetails.taxSettings?.salesTax
                    ? invoiceDetails.taxSettings.salesTax
                    : 0;
            // startDateUnix is available from the recurring payment details
            let startDateUnix: number | undefined;
            if (invoiceDetails.recurringPayments) {
                const startDateISO = invoiceDetails.recurringPayments.find(
                    (rp) => rp.careServiceId === item.careResourceId,
                )?.startDateISO;
                if (startDateISO) {
                    startDateUnix = dayjs(startDateISO).second() * 1000;
                }
            }
            return {
                careResourceId: item.careResourceId,
                description: item.careResourceName,
                code: code,
                amountCents: item.amountCents,
                quantity: item.quantity,
                discountPercent: item.discountPercent,
                priceAdjustmentCents: item.priceAdjustmentCents,
                applySalesTax: item.applyTax,
                taxRate: taxRate,
                startDateUnix: startDateUnix,
                serviceRecurrenceType: item.serviceRecurrenceType,
                recurrenceFrequencyUnit: item.serviceDetails?.recurrenceFrequencyUnit,
                occurrences: item.serviceDetails?.occurrences,
                careResourceType: item.careResourceType,
                totalAmountCents: item.totalAmountCents,
            };
        },
    );
    // Transform recurring payments
    let recurringPayments: payments.print.IRecurringPaymentDetails[] | undefined;
    if (invoiceDetails.recurringPayments) {
        recurringPayments = invoiceDetails.recurringPayments.map((rp) => ({
            id: rp.id,
            subscriptionId: `${invoiceDetails.accountPatient.id}-${rp.id}`,
            recurringAmountCents: rp.recurringAmountCents,
            startDateISO: rp.startDateISO,
            recurrenceFrequencyUnit: rp.recurrenceFrequencyUnit,
            numberOfOccurrences: rp.numberOfOccurrences,
            careServiceName: rp.careServiceName,
        }));
    }
    // Transform receipt details
    let receiptDetailsTransformed: payments.print.IPaymentRequest | undefined;
    if (receiptDetails) {
        receiptDetailsTransformed = {
            id: receiptDetails.id,
            amountCents: receiptDetails.amountCents,
            paymentDateISO: receiptDetails.paymentDateISO || "",
            paymentMethod: receiptDetails.paymentMethod
                ? {
                      id: receiptDetails.paymentMethod.id,
                      brand: receiptDetails.paymentMethod.brand,
                      last4: receiptDetails.paymentMethod.last4,
                      cardType: receiptDetails.paymentMethod.type,
                      expiryMonth: "", // Not available in old interface
                      expiryYear: "", // Not available in old interface
                  }
                : undefined,
        };
    }
    // Create business data
    const business: payments.print.IBusinessData = {
        name: account?.name ?? "",
        phoneNumber: location?.phoneNumber ?? "",
        address: location?.address.fullAddress ?? "",
    };
    // Get logo URL
    let logoUrl = undefined;
    if (location?.branding?.logoS3Url) {
        logoUrl = location.branding.logoS3Url;
    } else {
        logoUrl = account.branding?.logoS3Url ?? undefined;
    }
    return {
        invoiceNumber: invoiceDetails.invoiceNumber,
        issueDateISO: invoiceDetails.issueDateISO,
        dueDateISO: invoiceDetails.dueDateISO,
        totalAmountCents: invoiceDetails.totalAmountCents,
        subTotalAmountCents: invoiceDetails.subTotalAmountCents,
        accountPatient,
        provider,
        taxSettings,
        invoiceLineItems,
        recurringPayments,
        receiptDetails: receiptDetailsTransformed,
        business,
        logoUrl,
        timezone: location.timeZone,
        isCanada: account?.accountCountry === EAccountCountry.CANADA,
    };
};

/*
 * This is a separate function so the invoice generation logic used for both Staff and Patients
 */
export const generateInvoiceBlobWithData = async (
    invoiceDetails: business_integrations.IInvoiceDetails,
    services: InvoiceCharge[],
    items: InvoiceCharge[],
    locations: {
        id: number;
        timeZone: string;
        phoneNumber: string;
        branding: { logoS3Url?: string };
        address: { fullAddress: string };
    }[],
    account: { accountCountry: EAccountCountry; name: string; branding: { logoS3Url?: string } },
    receiptDetails?: business_integrations.IPaymentRequest,
) => {
    const location = locations.find((location_) => location_.id === invoiceDetails.locationId);
    if (!location) return;
    // Transform to new format
    const transformedInvoiceDetails = transformExistingPaymentDataIntoPrintFormat(
        invoiceDetails,
        services,
        items,
        locations,
        account,
        receiptDetails,
    );
    //https://github.com/diegomura/react-pdf/issues/736
    return await pdf(<Invoice invoiceDetails={transformedInvoiceDetails} />).toBlob();
};

/*
 * This is a separate function so the invoice generation logic used for both Staff and Patients
 */
export const generateInvoiceBlob = async (invoiceDetails: payments.print.IInvoicePrintData) => {
    //https://github.com/diegomura/react-pdf/issues/736
    return await pdf(<Invoice invoiceDetails={invoiceDetails} />).toBlob();
};

const PrintInvoiceButton: React.FC<IProps> = ({
    invoiceDetails,
    services,
    items,
    receiptDetails,
    buttonProps,
    toggleProps,
}) => {
    const { account } = useLobbieAccountRedux();
    const { locations } = useLobbieLocationsRedux();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [pdfUrl, setPdfUrl] = useState<string>("");

    const createPdfUrl = useCallback(
        async (_isInvoiceFinal?: boolean) => {
            // Clear old URL
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
            const blob = await generateInvoiceBlobWithData(
                invoiceDetails,
                services,
                items,
                locations,
                account,
                receiptDetails,
            );
            if (!blob) return "";
            return URL.createObjectURL(blob);
        },
        [account, invoiceDetails, locations, pdfUrl, receiptDetails, services, items],
    );

    const handleViewInvoice = useCallback(async () => {
        setIsLoading(true);
        const url = await createPdfUrl();
        setPdfUrl(url);
        setIsLoading(false);
    }, [createPdfUrl]);

    const handlePrint = useCallback(async () => {
        const url = await createPdfUrl();
        setPdfUrl(url);
        window.open(url, "_blank");
    }, [createPdfUrl]);

    // Clean up URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const viewInvoiceButton = useCallback(
        (isHeaderVariant = false) => {
            return (
                <ViewInvoiceButton
                    buttonProps={buttonProps}
                    toggleProps={toggleProps}
                    onViewInvoice={handleViewInvoice}
                    onPrint={handlePrint}
                    receiptDetails={receiptDetails}
                    isHeaderVariant={isHeaderVariant}
                    isLoading={isLoading}
                />
            );
        },
        [buttonProps, toggleProps, handleViewInvoice, handlePrint, isLoading, receiptDetails],
    );
    return <>{viewInvoiceButton(true)}</>;
};

export default PrintInvoiceButton;
